import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import type { Closure } from './schema';
import { ApmcScopeCell } from './components/cells/ApmcScopeCell';
import { DateRangeCell } from './components/cells/DateRangeCell';
import { RowActions } from './components/cells/RowActions';
import { StatusBadge } from './components/cells/StatusBadge';

export interface ClosureRowActions {
  onEdit: (row: Closure) => void;
  onDelete: (row: Closure) => void;
}

// `colId` on sortable columns matches the API's sortBy key. MarketClosuresGrid
// reads it from the column state and sends it as `sortBy`.
export function buildMarketClosuresColumns(
  actions: ClosureRowActions,
): ColDef<Closure>[] {
  return [
    {
      colId: 'name',
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 240,
      sortable: true,
    },
    {
      colId: 'startDate',
      field: 'startDate',
      headerName: 'Dates',
      width: 220,
      sortable: true,
      cellRenderer: DateRangeCell,
    },
    {
      field: 'apmcCount',
      headerName: 'Scope',
      width: 140,
      cellRenderer: ApmcScopeCell,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 130,
      cellRenderer: StatusBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Closure>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Closure>) =>
        p.data ? <RowActions row={p.data} {...actions} /> : null,
    },
  ];
}
